import { VenueReservation, VenueReservationAnswer } from "@/graphql/operations";
import type { ApolloCache, Reference, StoreObject } from "@apollo/client";
import type { Dispatch, RefObject, SetStateAction } from "react";
import type { ReservationsBucket } from "./reservationsTypes";

type BuildReservationAnswerUpdateOptions = {
  reservationId: string;
  answer: `${VenueReservationAnswer}` | null;
  userId?: string;
  options?: {
    venueId?: string;
    venueName?: string;
    venuePortalUserId?: string;
  };
  updateBucket: (
    key: string,
    updater: (bucket: ReservationsBucket) => ReservationsBucket,
  ) => ReservationsBucket;
  activeBucketKeyRef: RefObject<string>;
  setPendingReservations: Dispatch<SetStateAction<VenueReservation[]>>;
};

/**
 * Creates an Apollo cache update handler for reservation answer mutations.
 */
export const buildReservationAnswerUpdate = ({
  reservationId,
  answer,
  userId,
  options,
  updateBucket,
  activeBucketKeyRef,
  setPendingReservations,
}: BuildReservationAnswerUpdateOptions) => {
  const normalizedAnswer =
    answer === null
      ? null
      : answer === "ACCEPTED"
        ? VenueReservationAnswer.Accepted
        : VenueReservationAnswer.NotAccepted;
  /**
   * Builds an Apollo cache update function for a reservation answer mutation.
   */
  return (cache: ApolloCache<unknown>) => {
    let updatedReservation: VenueReservation | undefined;
    let didInsertPending = false;
    let didRemovePending = false;
    cache.modify({
      id: cache.identify({
        __typename: "VenueReservation",
        id: reservationId,
      }),
      fields: {
        answer: () => normalizedAnswer,
        reservationAnswerReceived: () =>
          normalizedAnswer ? new Date().toISOString() : null,
        venue: (prevVenue, { toReference, readField }) => {
          if (options?.venueId) {
            return toReference({
              __typename: "Venue",
              id: options.venueId,
            });
          }

          if (answer === null && prevVenue && readField("venuePortalUser")) {
            return null;
          }

          return prevVenue;
        },
      },
    });
    cache.modify({
      id: cache.identify({
        __typename: "VenuePortalUser",
        id: userId,
      }),
      fields: {
        pendingReservations: (
          existingRefs:
            | Reference
            | readonly (Reference | StoreObject | undefined)[]
            | undefined,
          { readField, toReference },
        ) => {
          const refs = Array.isArray(existingRefs)
            ? existingRefs.filter((ref): ref is Reference => Boolean(ref))
            : [];
          if (answer) {
            didRemovePending = refs.some(
              (ref) => readField("id", ref) === reservationId,
            );
            return refs;
          }
          const alreadyExists = refs.some(
            (ref) => readField("id", ref) === reservationId,
          );
          if (alreadyExists) return refs;
          didInsertPending = true;
          const nextRef = toReference({
            __typename: "VenueReservation",
            id: reservationId,
          });
          return nextRef ? [nextRef, ...refs] : refs;
        },
        pendingReservationsCount: (value: number) =>
          didInsertPending ? value + 1 : didRemovePending ? value - 1 : value,
      },
    });
    const updatedVenue =
      options?.venueId && options.venueName
        ? { __typename: "Venue" as const, id: options.venueId, name: options.venueName }
        : undefined;

    const activeBucketKey = activeBucketKeyRef.current;
    if (activeBucketKey) {
      updateBucket(activeBucketKey, (bucket) => {
        const activeBlock = bucket.blocks.find(
          (block) => block.id === bucket.activeBlockId,
        );
        if (!activeBlock) return bucket;
        activeBlock.reservations = activeBlock.reservations.map(
          (reservation) => {
            if (reservation.id !== reservationId) return reservation;
            updatedReservation = {
              ...reservation,
              answer: normalizedAnswer ?? null,
              reservationAnswerReceived: normalizedAnswer
                ? new Date().toISOString()
                : null,
              ...(updatedVenue && {
                venue: updatedVenue as VenueReservation["venue"],
              }),
            };
            return updatedReservation ?? reservation;
          },
        );
        return bucket;
      });
    }
    setPendingReservations((prev) => {
      const exists = prev.some(
        (reservation) => reservation.id === reservationId,
      );
      if (exists) {
        return prev.map((reservation) =>
          reservation.id === reservationId && updatedReservation
            ? updatedReservation
            : reservation,
        );
      }
      if (!answer && updatedReservation) {
        return [updatedReservation, ...prev];
      }
      return prev;
    });
  };
};
